<template>
  <v-card>
    <v-card-title primary-title>
      <div>
        <div class="headline secondary--text">
          {{
            monetization_pricing.id != undefined
              ? $t('screener.monetization.update_ticket')
              : $t('screener.monetization.create_ticket')
          }}
        </div>
        <span class="body-1 greyDarken1--text">{{
          $t('screener.monetization.group_ticket.create.subtitle')
        }}</span>
      </div>
      <v-spacer></v-spacer>
      <v-btn color="primary" outline @click="preview = true">{{
        $t('screener.btn.preview')
      }}</v-btn>
    </v-card-title>
    <v-card-text>
      <v-alert
        class="pa-2 mb-2"
        v-model="alert"
        color="primary"
        icon="info"
        outline
        dismissible
      >
        {{ $t('screener.monetization.group_ticket.info') }}
      </v-alert>
      <v-form ref="pricing_detail">
        <!-- Ticket name -->
        <v-text-field
          counter="250"
          prepend-icon="$vuetify.icons.rms_agreement"
          type="text"
          v-model="monetization_pricing.name"
          label="Ticket Name"
          :rules="[rules.required, rules.min_length(3), rules.max_length(250)]"
        ></v-text-field>

        <!-- Ticket Description -->
        <v-text-field
          counter="250"
          prepend-icon="$vuetify.icons.rms_agreement"
          type="text"
          v-model="monetization_pricing.description"
          name="description"
          label="Description"
          id="ticket_description"
          :rules="[rules.min_length(3), rules.max_length(250)]"
        ></v-text-field>

        <!-- Number of sales -->
        <v-text-field
          prepend-icon="$vuetify.icons.screener_group_ticket"
          type="number"
          v-model="monetization_pricing.number_of_sales"
          label="Number of sales"
          :rules="[
            rules.required,
            rules.numeric,
            rules.inBetween(5, 500, monetization_pricing.number_of_sales),
          ]"
        ></v-text-field>

        <!-- Screening selection -->
        <v-autocomplete
          v-model="monetization_pricing.screening_ids"
          :items="filteredScreenings | EventType(DEFINES.EVENT_TYPE.STANDARD_EVENTS)"
          prepend-icon="$vuetify.icons.rms_movie"
          multiple
          :rules="[rules.required, rules.min_length(2)]"
          chips
          :label="'Screenings'"
          item-text="title"
          color="primary"
          item-value="id"
        >
          <template v-slot:selection="data">
            <v-chip
              close
              class="chip--select-multi primary--text grey lighten-3"
              @input="removeSelectedScreening(data.item.id)"
            >
              <v-avatar v-if="data.item.logo_url">
                <img :src="data.item.logo_url" />
              </v-avatar>
              {{ data.item.title }}
            </v-chip>
          </template>
          <template v-slot:item="data">
            <template>
              <v-list-tile-avatar v-if="data.item.logo_url">
                <img :src="data.item.logo_url" />
              </v-list-tile-avatar>
              <v-list-tile-content>
                <v-list-tile-title v-html="data.item.title"></v-list-tile-title>
                <v-list-tile-sub-title
                  v-html="data.item.description"
                ></v-list-tile-sub-title>
              </v-list-tile-content>
            </template>
          </template>
        </v-autocomplete>

        <!-- Ticket Sale Start/End Date -->

        <!-- Start Date -->
        <DateTimePicker
          :label="
            $t(
              'screener.monetization.group_ticket.create.ticket_sale_start_date',
            )
          "
          datetime="purchasing_start_date"
          v-model="monetization_pricing.purchasing_start_date"
          :is_tooltip="true"
          :tooltip_content="$t('screener.monetization.ticket_sale_start_date')"
        />

        <ShowLocalTime
          :datetime="monetization_pricing.purchasing_start_date"
          :timezone="timezone"
        ></ShowLocalTime>

        <!-- End Date -->
        <DateTimePicker
          :label="
            $t('screener.monetization.group_ticket.create.ticket_sale_end_date')
          "
          datetime="purchasing_end_date"
          v-model="monetization_pricing.purchasing_end_date"
        />

        <ShowLocalTime
          :datetime="monetization_pricing.purchasing_end_date"
          :timezone="timezone"
        ></ShowLocalTime>

        <!-- Price -->
        <v-text-field
          :prefix="getCurrencySymbol()"
          :label="$t('screener.save.monetization.price')"
          :rules="[rules.required, rules.floating_number]"
          v-model="monetization_pricing.price"
          placeholder="0.00"
          :suffix="monetization_pricing.currency"
        >
          <template v-slot:append>
            <v-tooltip top color="white">
              <template v-slot:activator="{on}">
                <v-icon v-on="on">mdi-information-outline</v-icon>
              </template>
              <div
                class="grey--text text--darken-3 body-1"
                v-html="$t('screener.save.monetization.currency.tip')"
              ></div>
            </v-tooltip>
          </template>
        </v-text-field>

        <SystemShareView
          class="mt-1 mb-4"
          v-if="selected_company.commission_percentage > 0"
          :price="monetization_pricing.price"
          :currency="monetization_pricing.currency"
          :commissionPercentage="selected_company.commission_percentage"
        ></SystemShareView>

        <!-- Published / Unpublished -->
        <v-checkbox color="primary" v-model="monetization_pricing.published">
          <template v-slot:label>
            <div v-if="monetization_pricing.published">
              {{ $t('screener.save.form.published') }}
            </div>
            <div v-else>{{ $t('screener.save.form.unpublished') }}</div>
          </template>
          <template v-slot:append>
            <v-tooltip top color="white">
              <template v-slot:activator="{on}">
                <v-icon v-on="on">mdi-information-outline</v-icon>
              </template>
              <div class="grey--text text--darken-3 body-1">
                {{ $t('screener.group_ticket.form.published.tooltip') }}
              </div>
            </v-tooltip>
          </template>
        </v-checkbox>
      </v-form>
    </v-card-text>
    <v-divider></v-divider>
    <v-card-actions class="pa-3">
      <v-spacer></v-spacer>
      <v-btn
        @click="$router.push({name: 'group.ticket.index'})"
        color="grey"
        dark
        >{{ $t('rms.button.cancel') }}</v-btn
      >
      <v-btn
        v-if="monetization_pricing.id != undefined"
        @click="save('update')"
        color="secondary"
        dark
        >{{ $t('rms.button.update') }}</v-btn
      >
      <v-btn v-else @click="save('create')" color="secondary" dark>{{
        $t('rms.button.create')
      }}</v-btn>
    </v-card-actions>
    <GroupTicketPreview
      :passPreview.sync="preview"
      :ticket="monetization_pricing"
    ></GroupTicketPreview>
  </v-card>
</template>

<script>
import DEFINES from '@/defines';
import rules from '@/validation';
import i18n from '@/i18n';
import {mapState, mapGetters} from 'vuex';
import moment from 'moment';
import {utcToTz, tzToUtc, getCurrencySymbol, isDateFuture} from '@/helper';

// Components
import DateTimePicker from '@/components/DateTimePicker';
import ShowLocalTime from '@/components/ShowLocalTime';
import GroupTicketPreview from '@/components/screener/monetization/GroupTicketPreview';
import SystemShareView from '@/components/screener/monetization/SystemShareView';

export default {
  name: 'GroupTicketSave',
  data() {
    return {
      rules,
      alert: true,
      screenings: [],
      monetization_pricing: {
        name: null,
        company_id: null,
        description: null,
        screening_ids: [],
        type: DEFINES.MONETIZATION.PRICING_TYPE.GROUP_PURCHASE,
        festival_start_date: null,
        festival_end_date: null,
        purchasing_start_date: null,
        purchasing_end_date: null,
        number_of_sales: DEFINES.MONETIZATION.GROUP_PURCHASE.NUMBER_OF_SALES,
        price: null,
        currency: this.$store.state.company.company.currency,
        published: true,
      },
      preview: false,
    };
  },
  components: {
    DateTimePicker,
    ShowLocalTime,
    GroupTicketPreview,
    SystemShareView,
  },
  computed: {
    ...mapGetters('company', {
      selected_company_id: 'getSelected',
      timezone: 'getSelectedCompanyTimezone',
      selected_company: 'getSelectedCompany',
    }),

    filteredScreenings: function() {
      let currentTime = this.moment()
        .local()
        .format();
      return this.screenings.filter(
        (screener) =>
          this.moment
            .utc(screener.screening_ends_at)
            .add(screener.screening_tolerance, 'minutes')
            .local()
            .format() >= currentTime ||
          this.monetization_pricing.screening_ids.includes(screener.id),
      );
    },
  },
  created() {
    this.getAllScreeningsFromApi();
  },
  mounted() {
    this.get();
  },
  methods: {
    async get() {
      if (this.$route.params.pricing_id == undefined) {
        return;
      }
      let result = await this.$store.dispatch('monetization/getPricing', {
        pricing_id: this.$route.params.pricing_id,
      });
      var monetization_pricing = Object.assign({}, result);
      /**
       * convert utc time to company timezone
       * Can't convert it in input as user is filling his company timezone
       */
      monetization_pricing.purchasing_start_date = utcToTz(
        monetization_pricing.purchasing_start_date,
        this.timezone,
      );
      monetization_pricing.purchasing_end_date = utcToTz(
        monetization_pricing.purchasing_end_date,
        this.timezone,
      );

      this.monetization_pricing = monetization_pricing;
    },
    removeSelectedScreening(screening_id) {
      const index = this.monetization_pricing.screening_ids.indexOf(
        screening_id,
      );
      if (index >= 0) this.monetization_pricing.screening_ids.splice(index, 1);
    },
    getCurrencySymbol() {
      return getCurrencySymbol(this.monetization_pricing.currency);
    },
    async getAllScreeningsFromApi() {
      try {
        var response = await this.$store.dispatch('screener/list',{
          list_physical_events: false
        });
        this.screenings = response.screeners.data;
      } catch (error) {
        this.$store.dispatch('app/showMessage', {
          message: error.message,
          color: this.DEFINES.SNACKBAR_ERROR_COLOR,
        });
      }
    },
    async save(operation) {
      if (!this.$refs.pricing_detail.validate()) {
        return false;
      }

      try {
        // Setting company ID
        this.monetization_pricing.company_id = this.selected_company_id;

        var monetization_pricing = Object.assign({}, this.monetization_pricing);

        // convert company timezone to UTC
        monetization_pricing.purchasing_start_date = tzToUtc(
          monetization_pricing.purchasing_start_date,
          this.timezone,
        );
        monetization_pricing.purchasing_end_date = tzToUtc(
          monetization_pricing.purchasing_end_date,
          this.timezone,
        );

        // Validating screenings start date against the sale start date
        // Need UTC converted time
        this.validateScreeningsSchedule(
          monetization_pricing.purchasing_start_date,
        );

        var response = await this.$store.dispatch(
          'monetization/' + `${operation}GroupPricing`,
          monetization_pricing,
        );

        this.$router.push({
          name: 'group.ticket.index',
          params: {
            company_id: this.selected_company_id,
          },
        });
        this.$store.dispatch('app/showMessage', {
          message: i18n.t('screener.save.success.message'),
          color: this.DEFINES.SNACKBAR_SUCCESS_COLOR,
        });
      } catch (error) {
        this.$store.dispatch('app/showMessage', {
          message: error.message,
          color: 'error',
        });
      }
    },
    validateScreeningsSchedule(purchasing_start_date) {
      return true;
      var vue = this;
      this.monetization_pricing.screening_ids.forEach((screening_id) => {
        let sc = this.screenings.find((screening) => {
          return screening.id == screening_id;
        });
        if (
          sc &&
          !isDateFuture(sc.screening_starts_at, purchasing_start_date)
        ) {
          throw {
            message:
              'The screening ' +
              sc.title +
              ' must have the start date after the sale start date',
          };
        }
      });
    },
  },
};
</script>

<style></style>
